import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ModalForm from '#app/components/ModalForm/ModalForm';
import {
  MobileMenuContent,
  MobileMenuItem,
  MobileMenuSection,
  MobileMenuDivider,
  MobileMenuLink,
} from '../styles';
import { InputContainer, Search } from 'finform';
import { Text } from 'finhelper';
import { handleRouteClick } from '#app/interface/cabinet/header/Dropdown/utils/handleRouteClick';
import { isManager } from '#app/helpers/isManager';
import { routes } from '#app/constants/routes';
import useLogout from '#app/hooks/useLogout';
import SmallProfileImage from '#app/components/ProfileImage/SmallProfileImage';
import { Points, HeaderIcon, Warning } from '../styles';
import { useAppDispatch } from '#app/hooks/useAppStore';
import { useQueryClient } from '@tanstack/react-query';
import refreshAllUserData from '#app/helpers/refreshAllUserData';

const TEACHER_ICON_PATH = require('../../../../assets/icons/teacher.svg');
const TEACHER_RED_ICON_PATH = require('../../../../assets/icons/teacher_red.svg');
const WARNING_ICON_PATH = require('../../../../assets/icons/warning-2.svg');

interface MobileMenuProps {
  modalVisible: boolean;
  closeMenu: () => void;
  i18n: any;
  data: any;
  client: any;
  clients: any[];
  searchedClients: any[];
  clientLoading: boolean;
  currencyLoading: boolean;
  accountLoading: boolean;
  formLoading: boolean;
  incomeTypesLoading: boolean;
  spendTypesLoading: boolean;
  tagsLoading: boolean;
  subcategoriesLoading: boolean;
  demo: any;
  tutorial: any;
  notifications: any[];
  logout: any;
  handleClient: (params: any) => void;
  getUser: () => void;
  finishScenario: (params: any) => void;
  handleEndDemo: () => void;
  handleEndTutorial: () => void;
  startScenario: (params: any) => void;
  form: any;
  setForm: (form: any) => void;
  searchLoading: boolean;
  setSearchLoading: (loading: boolean) => void;
  handleSearchClients: (searchText: string) => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  modalVisible,
  closeMenu,
  i18n,
  data,
  client,
  clients,
  searchedClients,
  clientLoading,
  currencyLoading,
  accountLoading,
  formLoading,
  incomeTypesLoading,
  spendTypesLoading,
  tagsLoading,
  subcategoriesLoading,
  demo,
  tutorial,
  notifications,
  logout,
  handleClient,
  getUser,
  finishScenario,
  handleEndDemo,
  handleEndTutorial,
  startScenario,
  form,
  setForm,
  searchLoading,
  setSearchLoading,
  handleSearchClients,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const handleLogout = useLogout(logout);

  const isLoaded =
    !clientLoading &&
    !currencyLoading &&
    !accountLoading &&
    !formLoading &&
    !incomeTypesLoading &&
    !spendTypesLoading &&
    !tagsLoading &&
    !subcategoriesLoading;

  const handleMenu = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    handleRouteClick(url, client, data, navigate);
    closeMenu();
  };

  const clientsToShow = clients || [];

  // Navigation menu items
  const accountingLinks = [
    { link: routes.accounting.dashboard, text: i18n.menu.dashboard },
    { link: routes.accounting.values, text: i18n.menu.values },
    { link: routes.accounting.dynamic, text: i18n.menu.dynamic },
    { link: routes.accounting.accountsPropertiesObligations, text: i18n.menu.accounts },
    { link: routes.accounting.logs, text: i18n.menu.operations },
  ];

  if (isManager(data)) {
    accountingLinks.push({ link: routes.accounting.clients, text: i18n.menu.clients });
  }

  const incomeLinks = [
    { link: routes.incomeAndExpenses.tactics, text: i18n.menu.dashboard },
    { link: routes.incomeAndExpenses.income, text: i18n.menu.incomes },
    { link: routes.incomeAndExpenses.spendings, text: i18n.menu.outcomes },
  ];

  const planLinks = [
    { link: routes.plan.plan, text: i18n.menu.dashboard },
    { link: routes.plan.salaryPlan, text: i18n.menu.plan },
    { link: routes.plan.goals, text: i18n.menu.goals },
    { link: routes.plan.insurances, text: 'Страхование' },
  ];

  const investingLinks = [
    { link: routes.investing.case, text: i18n.menu.dashboard },
    { link: routes.investing.chart(), text: i18n.menu.charts },
    { link: routes.investing.invest, text: i18n.menu.investList },
    { link: routes.investing.paymentsCalendar, text: i18n.menu.paymentsСalendar },
    { link: routes.investing.diagrams, text: i18n.menu.diagrams },
  ];

  return (
    <ModalForm
      modalVisible={modalVisible}
      closeForm={closeMenu}
      header="Меню"
      width="90%"
      height="auto"
      disableAnimation={true}
    >
      <MobileMenuContent>
        {/* Profile and Logout */}
        <MobileMenuSection className="row">
          <MobileMenuItem onClick={(e) => handleMenu(e, routes.register.page1)}>
            <SmallProfileImage />
            <span>{i18n.menu.profile}</span>
          </MobileMenuItem>
          <MobileMenuItem
            className="push-right"
            onClick={(e) => {
              e.preventDefault();
              handleLogout(e);
              closeMenu();
            }}
          >
            <span>{i18n.menu.logout}</span>
          </MobileMenuItem>
        </MobileMenuSection>

        <MobileMenuDivider />

        {/* Navigation Menu */}
        <MobileMenuSection>
          <MobileMenuLink onClick={(e) => handleMenu(e, routes.accounting.dashboard)}>
            {i18n.menu.view}
          </MobileMenuLink>
          {accountingLinks.map((item, index) => (
            <MobileMenuItem key={index} onClick={(e) => handleMenu(e, item.link)}>
              <span>{item.text}</span>
            </MobileMenuItem>
          ))}

          <MobileMenuLink onClick={(e) => handleMenu(e, routes.incomeAndExpenses.tactics)}>
            {i18n.menu.incomesOutcomes}
          </MobileMenuLink>
          {incomeLinks.map((item, index) => (
            <MobileMenuItem key={index} onClick={(e) => handleMenu(e, item.link)}>
              <span>{item.text}</span>
            </MobileMenuItem>
          ))}

          <MobileMenuLink onClick={(e) => handleMenu(e, routes.plan.plan)}>
            {i18n.menu.planing}
          </MobileMenuLink>
          {planLinks.map((item, index) => (
            <MobileMenuItem key={index} onClick={(e) => handleMenu(e, item.link)}>
              <span>{item.text}</span>
            </MobileMenuItem>
          ))}

          <MobileMenuLink onClick={(e) => handleMenu(e, routes.investing.case)}>
            {i18n.menu.invest}
          </MobileMenuLink>
          {investingLinks.map((item, index) => (
            <MobileMenuItem key={index} onClick={(e) => handleMenu(e, item.link)}>
              <span>{item.text}</span>
            </MobileMenuItem>
          ))}

          <MobileMenuLink onClick={(e) => handleMenu(e, routes.config.index)}>
            {i18n.menu.config}
          </MobileMenuLink>
        </MobileMenuSection>

        <MobileMenuDivider />

        {/* Client Search (for managers) */}
        {isManager(data) && (
          <>
            <MobileMenuSection>
              <InputContainer>
                <Search
                  placeholder={'Клиент'}
                  id="client_id_mobile"
                  name="client_id_mobile"
                  search={form.item_search}
                  onSearch={(searchText) => {
                    setForm({
                      ...form,
                      item_search: searchText,
                    });

                    if (searchText && searchText.length >= 2) {
                      handleSearchClients(searchText);
                    }
                  }}
                  loading={searchLoading || (!isLoaded && form.item)}
                  disabled={!isLoaded && form.item}
                  value={form?.id}
                  onChange={(item) => {
                    if (item) {
                      setForm({
                        item: item,
                        item_id: item.id,
                        item_search: Text.fio(item) + (item.email ? ' (' + item.email + ')' : ''),
                      });

                      handleClient({ client: item, data });
                      void refreshAllUserData({
                        queryClient,
                        dispatch,
                        client: item,
                      });
                      getUser();
                      closeMenu();
                    } else {
                      setForm({
                        item: null,
                        item_id: '',
                        item_search: '',
                      });
                    }
                  }}
                  options={clientsToShow?.map((user) => {
                    return {
                      ...user.attributes,
                      name: Text.fio(user) + (user.email ? ' (' + user.email + ')' : ''),
                    };
                  })}
                />
              </InputContainer>
            </MobileMenuSection>
            <MobileMenuDivider />
          </>
        )}

        {/* Points, Teach, Notifications */}
        <MobileMenuSection>
          {client?.points > 0 && (
            <MobileMenuItem>
              <Points id="user-points-mobile">{client.points ? client.points : 0}</Points>
            </MobileMenuItem>
          )}
          {!isManager(data) && (
            <MobileMenuItem
              onClick={(e) => {
                e.preventDefault();
                if (demo || tutorial) {
                  finishScenario({
                    scenario: 'register',
                    callback: () => {
                      handleEndDemo();
                      handleEndTutorial();
                      if (location.pathname + location.search !== routes.register.page1) {
                        navigate(routes.register.page1);
                      }
                      closeMenu();
                    },
                  });
                } else {
                  startScenario({
                    scenario: 'register',
                    callback: () => {},
                  });
                  closeMenu();
                }
              }}
            >
              <HeaderIcon style={{ backgroundColor: demo || tutorial ? '#F8BFC6' : '#95DCFB' }}>
                <Warning
                  src={demo || tutorial ? TEACHER_RED_ICON_PATH : TEACHER_ICON_PATH}
                  alt="teacher-icon"
                />
              </HeaderIcon>
              <span>{demo || tutorial ? 'Завершить обучение' : 'Обучение'}</span>
            </MobileMenuItem>
          )}
          <MobileMenuItem
            onClick={(e) => {
              e.preventDefault();
              handleMenu(e, routes.common.notifications);
            }}
          >
            <HeaderIcon>
              <Warning src={WARNING_ICON_PATH} alt="" />
            </HeaderIcon>
            <span>Уведомления {notifications?.length > 0 ? `(${notifications.length})` : ''}</span>
          </MobileMenuItem>
        </MobileMenuSection>
      </MobileMenuContent>
    </ModalForm>
  );
};

export default MobileMenu;
